// APERÇU JETABLE — sera remplacé par src/human (sous-agent A).
// Charge le maillage MakeHuman brut, applique les cibles "femme jeune", ajuste quelques proxies
// (yeux, sourcils, cils, cheveux, tenue) sans squelette, pour régler rendu et lumière sur un vrai corps.
import * as THREE from 'three';
import type { MaterialProvider, MaterialKind } from '../human/types';
import { createCornea } from '../render/materials/provider';

const MH = '/.vendor/mh/makehuman/data/';
const SYS = '/.vendor/ex/makehuman_system_assets/';
const EX = '/.vendor/ex/';

interface Obj { v: number[]; vt: number[]; faces: Array<{ g: string; v: number[]; t: number[] }> }

async function text(url: string) {
  const r = await fetch(url);
  if (!r.ok) throw new Error(url + ' ' + r.status);
  return r.text();
}

function parseObj(src: string): Obj {
  const v: number[] = [], vt: number[] = [], faces: Obj['faces'] = [];
  let g = '';
  for (const line of src.split('\n')) {
    const c = line.charCodeAt(0);
    if (c === 118) { // v
      const p = line.trim().split(/\s+/);
      if (p[0] === 'v') v.push(+p[1], +p[2], +p[3]);
      else if (p[0] === 'vt') vt.push(+p[1], +p[2]);
    } else if (c === 102) { // f
      const p = line.trim().split(/\s+/).slice(1);
      const fv: number[] = [], ft: number[] = [];
      for (const s of p) { const [a, b] = s.split('/'); fv.push(+a - 1); ft.push(b ? +b - 1 : -1); }
      faces.push({ g, v: fv, t: ft });
    } else if (c === 103) g = line.trim().split(/\s+/)[1] ?? '';
  }
  return { v, vt, faces };
}

function applyTarget(pos: Float32Array, src: string, w: number) {
  for (const line of src.split('\n')) {
    if (!line || line[0] === '#') continue;
    const p = line.trim().split(/\s+/);
    if (p.length < 4) continue;
    const i = +p[0] * 3;
    pos[i] += +p[1] * w; pos[i + 1] += +p[2] * w; pos[i + 2] += +p[3] * w;
  }
}

/** Géométrie indexée à partir de faces (quads triangulés), positions fournies par sommet "source". */
function buildGeometry(obj: Obj, positions: Float32Array, filter: (g: string, v: number[]) => boolean): THREE.BufferGeometry {
  const key = new Map<number, number>();
  const outPos: number[] = [], outUv: number[] = [], index: number[] = [];
  const nvt = obj.vt.length / 2 + 1;
  for (const f of obj.faces) {
    if (!filter(f.g, f.v)) continue;
    const ids = f.v.map((vi, k) => {
      const ti = f.t[k];
      const kk = vi * nvt + (ti + 1);
      let id = key.get(kk);
      if (id === undefined) {
        id = outPos.length / 3;
        key.set(kk, id);
        outPos.push(positions[vi * 3], positions[vi * 3 + 1], positions[vi * 3 + 2]);
        outUv.push(ti >= 0 ? obj.vt[ti * 2] : 0, ti >= 0 ? obj.vt[ti * 2 + 1] : 0);
      }
      return id;
    });
    for (let k = 1; k + 1 < ids.length; k++) index.push(ids[0], ids[k], ids[k + 1]);
  }
  const g = new THREE.BufferGeometry();
  g.setAttribute('position', new THREE.Float32BufferAttribute(outPos, 3));
  g.setAttribute('uv', new THREE.Float32BufferAttribute(outUv, 2));
  g.setIndex(index);
  g.computeVertexNormals();
  return g;
}

function parseMhmat(src: string) {
  const o: Record<string, string> = {};
  for (const line of src.split('\n')) {
    const t = line.trim();
    if (!t || t[0] === '#') continue;
    const i = t.indexOf(' ');
    if (i > 0) o[t.slice(0, i)] = t.slice(i + 1).trim();
  }
  return o;
}

async function proxy(dir: string, file: string, base: Float32Array, kind: MaterialKind, provider: MaterialProvider, matOverride?: string) {
  const src = await text(dir + file);
  const refs: number[][] = [];
  const head: Record<string, string> = {};
  const del = new Set<number>();
  let inVerts = false, inDel = false;
  for (const line of src.split('\n')) {
    const t = line.trim();
    if (!t || t[0] === '#') continue;
    const p = t.split(/\s+/);
    const numeric = p.every((x) => /^-?[\d.]+(e-?\d+)?$/.test(x));
    if (p[0] === 'verts') { inVerts = true; inDel = false; continue; }
    if (p[0] === 'delete_verts') { inVerts = false; inDel = true; continue; }
    if (inDel && /^[\d\s-]+$/.test(t)) {
      for (let k = 0; k < p.length; k++) {
        if (p[k + 1] === '-') { for (let v = +p[k]; v <= +p[k + 2]; v++) del.add(v); k += 2; }
        else del.add(+p[k]);
      }
      continue;
    }
    if (inVerts && numeric && (p.length === 9 || p.length === 1)) { refs.push(p.map(Number)); continue; }
    if (!numeric) head[p[0]] = p.slice(1).join(' ');
  }
  const scale = (k: string, axis: number) => {
    const s = head[k]?.split(/\s+/).map(Number);
    if (!s) return 1;
    return Math.abs(base[s[0] * 3 + axis] - base[s[1] * 3 + axis]) / s[2];
  };
  const sx = scale('x_scale', 0), sy = scale('y_scale', 1), sz = scale('z_scale', 2);
  const pos = new Float32Array(refs.length * 3);
  refs.forEach((r, i) => {
    if (r.length === 1) { pos.set(base.subarray(r[0] * 3, r[0] * 3 + 3), i * 3); return; }
    for (let a = 0; a < 3; a++) {
      pos[i * 3 + a] = r[3] * base[r[0] * 3 + a] + r[4] * base[r[1] * 3 + a] + r[5] * base[r[2] * 3 + a] + r[6 + a] * [sx, sy, sz][a];
    }
  });
  const obj = parseObj(await text(dir + head['obj_file']));
  const geo = buildGeometry(obj, pos, () => true);
  const matFile = matOverride ?? head['material'];
  const mat = parseMhmat(await text(new URL(matFile, location.origin + dir).pathname));
  const matDir = new URL(matFile, location.origin + dir).pathname.replace(/[^/]+$/, '');
  const tex = (k: string) => (mat[k] ? matDir + mat[k] : undefined);
  const material = provider({
    kind, assetId: head['name'] ?? file,
    textures: { diffuse: tex('diffuseTexture'), normal: tex('normalmapTexture'), ao: tex('aomapTexture') },
    diffuseColor: (mat['diffuseColor']?.split(/\s+/).map(Number) as [number, number, number]) ?? [1, 1, 1], transparent: mat['transparent'] === 'True', alphaToCoverage: true,
    backfaceCull: mat['backfaceCull'] !== 'False', extra: {},
  });
  const mesh = new THREE.Mesh(geo, material);
  mesh.castShadow = kind !== 'eyelashes' && kind !== 'eyebrows';
  mesh.receiveShadow = true;
  mesh.renderOrder = Number(head['z_depth'] ?? 0);
  mesh.userData.deleteVerts = del;
  return mesh;
}

export async function quickHuman(provider: MaterialProvider, onProgress: (p: number, label: string) => void) {
  onProgress(0.05, 'Maillage de base…');
  const obj = parseObj(await text(MH + '3dobjs/base.obj'));
  const base = new Float32Array(obj.v);
  onProgress(0.3, 'Morphologie féminine…');
  const T = MH + 'targets/macrodetails/';
  const targets: Array<[string, number]> = [
    [T + 'universal-female-young-averagemuscle-averageweight.target', 1],
    [T + 'caucasian-female-young.target', 1 / 3],
    [T + 'african-female-young.target', 1 / 3],
    [T + 'asian-female-young.target', 1 / 3],
  ];
  // Retouches "beauté douce" (préfiguration du préréglage par défaut du mode Création).
  const D = MH + 'targets/';
  const beauty: Array<[string, number]> = [
    ['eyes/l-eye-scale-incr', 0.3], ['eyes/r-eye-scale-incr', 0.3],
    ['eyes/l-eye-corner2-up', 0.25], ['eyes/r-eye-corner2-up', 0.25],
    ['mouth/mouth-upperlip-volume-incr', 0.2], ['mouth/mouth-lowerlip-volume-incr', 0.15],
    ['mouth/mouth-cupidsbow-incr', 0.3], ['mouth/mouth-angles-up', 0.12],
    ['nose/nose-scale-horiz-decr', 0.25], ['nose/nose-nostrils-width-decr', 0.3], ['nose/nose-point-width-decr', 0.25], ['nose/nose-hump-decr', 0.3],
    ['chin/chin-width-decr', 0.25], ['chin/chin-prominent-decr', 0.1],
    ['head/head-oval', 0.3],
    ['cheek/l-cheek-bones-incr', 0.2], ['cheek/r-cheek-bones-incr', 0.2],
  ];
  for (const [n, w] of beauty) targets.push([D + n + '.target', w]);
  const srcs = await Promise.all(targets.map(([u]) => text(u).catch(() => '')));
  srcs.forEach((s, i) => applyTarget(base, s, targets[i][1]));

  onProgress(0.5, 'Peau…');
  const skin = provider({
    kind: 'skin', assetId: 'young_caucasian_female',
    textures: { diffuse: EX + 'skins01/skins/toigo_light_skin_with_natural_makeup/young_lightskinned_female_diffuse4.png' },
    diffuseColor: [1, 1, 1], transparent: false, alphaToCoverage: false, backfaceCull: true, extra: {},
  });
  const group = new THREE.Group();
  const body = new THREE.Mesh(buildGeometry(obj, base, (g) => g === 'body'), skin);
  body.castShadow = body.receiveShadow = true;
  group.add(body);

  onProgress(0.6, 'Yeux, sourcils, cils…');
  const parts = await Promise.all([
    proxy(SYS + 'eyes/high-poly/', 'high-poly.mhclo', base, 'eyes', provider, '../materials/brownlight.mhmat'),
    proxy(EX + 'eyebrows01/eyebrows/mindfront_eyebrows_05/', 'mindfront_eyebrows_05.mhclo', base, 'eyebrows', provider),
    proxy(EX + 'eyelashes01/eyelashes/mindfront_eyelashes_02/', 'mindfront_eyelashes_02.mhclo', base, 'eyelashes', provider),
  ]);
  onProgress(0.8, 'Coiffure et tenue…');
  parts.push(...await Promise.all([
    proxy(EX + 'hair01/hair/toigo_blunt_bob_with_bangs/', 'toigo_blunt_bob_with_bangs.mhclo', base, 'hair', provider),
    proxy(SYS + 'clothes/female_casualsuit01/', 'female_casualsuit01.mhclo', base, 'clothes', provider),
  ]));
  for (const p of parts) group.add(p);
  // Masque les faces du corps entièrement couvertes par un vêtement (règle MakeHuman : tous les sommets masqués).
  const del = new Set<number>();
  for (const p of parts) for (const v of (p.userData.deleteVerts as Set<number>)) del.add(v);
  if (del.size) {
    body.geometry.dispose();
    body.geometry = buildGeometry(obj, base, (g, v) => g === 'body' && !v.every((i) => del.has(i)));
  }
  group.add(createCornea(parts[0]));

  // Décimètres → mètres, pieds au sol.
  group.scale.setScalar(0.1);
  body.geometry.computeBoundingBox();
  group.position.y = -body.geometry.boundingBox!.min.y * 0.1;
  onProgress(1, 'Prête.');
  return group;
}
